import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { ChevronRightIcon, PackagePlusIcon } from '@/shared/ui/icons';
import { extractErrorMessage } from '@/shared/api/errors';
import { useToast } from '@/shared/hooks/useToast';
import { useParcelsAvailableForSplit } from '@/entities/parcels';
import { useCreateSplit } from '@/entities/splits';

export const WidgetsServicesSplitMain = () => {
  const { t } = useTranslation('services');
  const navigate = useNavigate();
  const toast = useToast();

  const { data, isLoading, isError, error, refetch } = useParcelsAvailableForSplit();
  const parcels = data ?? [];

  const createSplit = useCreateSplit();

  const handleSelect = (parcel: (typeof parcels)[number]) => {
    createSplit.mutate(
      { parcel: parcel.id },
      {
        onSuccess: (split) => navigate(`/splits/${split.id}`),
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  return (
    <>
      <PageHeader title={t('splitParcel')} showBack backTo="/settings/services" />
      <div className="flex flex-col gap-3 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <ErrorState message={extractErrorMessage(error)} onRetry={() => void refetch()} />
        ) : parcels.length === 0 ? (
          <EmptyState
            icon={<PackagePlusIcon className="h-10 w-10" aria-hidden />}
            title={t('noParcelsAvailable')}
          />
        ) : (
          <>
            <p className="text-sm font-medium text-ink-muted">{t('selectParcel')}</p>
            {parcels.map((parcel) => (
              <button
                key={parcel.id}
                type="button"
                disabled={createSplit.isPending}
                onClick={() => handleSelect(parcel)}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-card transition-colors hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-slate-100 text-ink-faint">
                  <PackagePlusIcon className="h-7 w-7" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-ink">{parcel.name}</p>
                  <p className="truncate text-xs text-ink-muted">{parcel.trek_id}</p>
                  <p className="mt-1.5 truncate text-xs text-ink-muted">{t(`parcelStatus:${parcel.status}`)}</p>
                </div>
                <ChevronRightIcon className="h-5 w-5 shrink-0 text-ink-faint" aria-hidden />
              </button>
            ))}
          </>
        )}
      </div>
    </>
  );
};
